/**
 * Fully custom dropdown — our trigger AND our option list.
 *
 * The sibling `Select` primitive keeps a NATIVE <select> inside our chrome, so
 * its OPEN list is drawn by the OS and looks nothing like the app. This one
 * paints both halves: the closed box reuses `.select-wrap` / `.select-native`
 * verbatim (one source of truth — the two controls cannot drift apart), and
 * the open menu is a portaled listbox in the app's popover vocabulary
 * (`.rail-project-menu`'s surface, `.roster-picker-opt.selected`'s accent row).
 *
 * Portal to <body> with `position: fixed` for the same reason HelpTip does it:
 * an in-flow menu is clipped by any ancestor with `overflow: auto/hidden` — the
 * inbox panes, cards, and scroll containers — and no z-index rescues it. It
 * opens below and flips above only when a real measurement says it would clip
 * (useLayoutEffect, before paint), and re-places on scroll/resize while open;
 * the scroll listener is capture-phase because the panes scroll internally,
 * not the window.
 *
 * Focus never leaves the trigger. Arrows move an `aria-activedescendant`
 * highlight over the options — the same pattern the help search listbox uses —
 * so the control keeps one tab stop and screen readers still announce the
 * moving row.
 *
 * Cost, stated plainly: unlike `Select` this has no mobile picker and no OS
 * type-ahead. It reimplements type-ahead below; on a phone it renders the same
 * menu as on desktop. Use it where the open list is on screen a lot (the PDF
 * Inbox); `Select` is still right everywhere else.
 */
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface DropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  /** Trigger label when `value` matches no option. */
  placeholder?: string;
  disabled?: boolean;
  /** Visual scale. `md` (default) = 32px; `sm` = 26px for dense rows. */
  uiSize?: 'md' | 'sm';
  /** Extra classes on the wrapper. */
  className?: string;
  /** Layout styling — width, flex, margin. Lands on the wrapper. */
  style?: React.CSSProperties;
  /** Escape hatch for the trigger itself (mirrors Select's `selectClassName`). */
  triggerClassName?: string;
  /** Escape hatch for the trigger itself (mirrors Select's `selectStyle`) —
   *  e.g. an accent border while a filter is engaged. */
  triggerStyle?: React.CSSProperties;
  /** Renders a small ✕ inside the control, left of the chevron, that calls
   *  this instead of opening the menu. Pass it only while there is something
   *  to clear — the caller owns that decision (see the inbox filter bar). */
  onClear?: () => void;
  /** Accessible name for the ✕. Required with `onClear` in spirit — defaults
   *  to a bare "Clear" that says nothing about WHAT it clears. */
  clearLabel?: string;
  'aria-label'?: string;
}

/** Gap between trigger and menu, and the viewport margin the menu keeps. */
const GAP = 4;
const EDGE = 8;
const MAX_H = 320;
const MAX_W = 480;
/** Floor for the menu box so a cramped side still shows a scrollable list
 *  rather than a 20px sliver. */
const MIN_H = 120;
const TYPEAHEAD_RESET_MS = 500;

interface MenuPos {
  left: number;
  /** Trigger width — the menu's floor, so it never reads narrower than the
   *  box it came from. Content (capped at MAX_W) may make it wider. */
  minWidth: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
  up: boolean;
}

const samePos = (a: MenuPos, b: MenuPos) =>
  a.left === b.left &&
  a.minWidth === b.minWidth &&
  a.top === b.top &&
  a.bottom === b.bottom &&
  a.maxHeight === b.maxHeight &&
  a.up === b.up;

let dropdownSeq = 0;

export const Dropdown = forwardRef<HTMLButtonElement, DropdownProps>(function Dropdown(
  {
    options,
    value,
    onChange,
    placeholder,
    disabled,
    uiSize = 'md',
    className,
    style,
    triggerClassName,
    triggerStyle,
    onClear,
    clearLabel,
    'aria-label': ariaLabel,
  },
  forwardedRef,
) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Exposed so a call site can ask "is my own control the focused element?" —
  // the inline cells' draft-resync guard depends on that question.
  useImperativeHandle(forwardedRef, () => triggerRef.current as HTMLButtonElement, []);

  const idRef = useRef<string>();
  if (idRef.current === undefined) idRef.current = `dropdown-${++dropdownSeq}`;
  const id = idRef.current;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<MenuPos>({ left: 0, minWidth: 0, maxHeight: MAX_H, up: false });

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const label = selected ? selected.label : (placeholder ?? '');

  const typeahead = useRef({ buffer: '', at: 0 });

  /* ── positioning ──────────────────────────────────────────────────────── */

  // One function for open, scroll, and resize. On the first pass the menu is
  // not in the DOM yet, so `up` is false by construction — that's the point:
  // we place BELOW, then this runs again from the layout effect with a real
  // height and flips only if the real box would actually clip.
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const menu = menuRef.current;
    const spaceBelow = window.innerHeight - r.bottom - GAP - EDGE;
    const spaceAbove = r.top - GAP - EDGE;
    const wanted = menu ? Math.min(menu.scrollHeight, MAX_H) : 0;
    const up = wanted > spaceBelow && spaceAbove > spaceBelow;
    const maxHeight = Math.max(MIN_H, Math.min(MAX_H, up ? spaceAbove : spaceBelow));
    // Width is content-driven between the trigger's width and MAX_W, so the
    // left clamp needs the measured box; pre-mount, the trigger's own width is
    // the honest stand-in.
    const w = menu ? menu.getBoundingClientRect().width : r.width;
    const left = Math.max(EDGE, Math.min(r.left, window.innerWidth - EDGE - w));
    const next: MenuPos = {
      left,
      minWidth: r.width,
      maxHeight,
      up,
      top: up ? undefined : r.bottom + GAP,
      bottom: up ? window.innerHeight - r.top + GAP : undefined,
    };
    setPos((prev) => (samePos(prev, next) ? prev : next));
  }, []);

  // Measure-then-correct, before paint — the clipped frame is never shown.
  useLayoutEffect(() => {
    if (!open) return;
    place();
  });

  useEffect(() => {
    if (!open) return;
    // Capture phase: the inbox master/detail panes scroll internally, and a
    // bubbling scroll listener on window never hears those.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  /* ── open / close / commit ────────────────────────────────────────────── */

  const firstEnabled = (from: number, step: number) => {
    for (let i = from; i >= 0 && i < options.length; i += step) {
      if (!options[i]?.disabled) return i;
    }
    return -1;
  };

  const openMenu = () => {
    if (disabled) return;
    // Land on the current value, or the first thing that can be picked.
    const start = selected && !selected.disabled ? selectedIndex : firstEnabled(0, 1);
    setActive(start);
    typeahead.current = { buffer: '', at: 0 };
    place();
    setOpen(true);
  };

  const closeMenu = (refocus = true) => {
    setOpen(false);
    setActive(-1);
    if (refocus) triggerRef.current?.focus();
  };

  const commit = (index: number) => {
    const opt = options[index];
    if (!opt || opt.disabled) return;
    closeMenu();
    // Re-picking the current value is not a change. Call sites lean on this
    // (the inline cells only fire their save when the value actually moved).
    if (opt.value !== value) onChange(opt.value);
  };

  const moveTo = (index: number) => {
    if (index < 0) return;
    setActive(index);
  };

  // Highlight follows the keyboard; `nearest` so a move inside the visible
  // window doesn't jerk the list.
  useEffect(() => {
    if (!open || active < 0) return;
    menuRef.current
      ?.querySelector(`#${id}-opt-${active}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, active, id]);

  // Outside click. mousedown (not click) so the menu is gone before the click
  // lands on whatever was underneath it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      // No refocus — the user is deliberately going somewhere else.
      closeMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  /* ── keyboard ─────────────────────────────────────────────────────────── */

  // Two-tier match. Tier 1 is a plain label prefix — the common case, and the
  // cheapest. Tier 2 only runs when tier 1 comes up empty: it looks for the
  // buffer at the START OF A WORD anywhere in the label (split on space /
  // dash / parens), so a list where every option shares the same leading
  // text (e.g. "PCO 029 — Repair Trew Culvert", "PCO 031 — …") still lets
  // you type the distinguishing part — the number, or a word further in —
  // instead of only the shared "PCO " prefix. Both tiers are one findIndex
  // pass, so this stays O(n) in the option count per keypress.
  const runTypeahead = (char: string) => {
    const now = Date.now();
    const t = typeahead.current;
    t.buffer = now - t.at > TYPEAHEAD_RESET_MS ? char : t.buffer + char;
    t.at = now;
    const needle = t.buffer.toLowerCase();
    let hit = options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(needle));
    if (hit < 0) {
      hit = options.findIndex(
        (o) =>
          !o.disabled &&
          o.label
            .toLowerCase()
            .split(/[\s\-()]+/)
            .some((word) => word.startsWith(needle)),
      );
    }
    if (hit >= 0) setActive(hit);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // Ctrl/Cmd+Enter is Save in the dialogs this control sits in, never a
        // request to open the menu. Swallowing it here is the likeliest way
        // to lose it: several of those dialogs focus this trigger on mount,
        // so it is what the shortcut hits first.
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        openMenu();
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeMenu();
        return;
      case 'Tab':
        // Not prevented — Tab still moves focus, it just doesn't leave a menu
        // hanging over the page.
        closeMenu(false);
        return;
      case 'Enter':
      case ' ':
        // Same rule with the menu open, and it matters more here: the active
        // option follows the pointer, so committing on Ctrl/Cmd+Enter would
        // change WHICH record the save that follows is about — and then let
        // the save run anyway, because this handler bubbles.
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        commit(active);
        return;
      case 'ArrowDown':
        e.preventDefault();
        // No wrap — native <select> doesn't wrap, and a wrapping list makes
        // "hold Down to reach the end" silently overshoot to the top.
        moveTo(firstEnabled(active + 1, 1));
        return;
      case 'ArrowUp':
        e.preventDefault();
        moveTo(firstEnabled(active - 1, -1));
        return;
      case 'Home':
        e.preventDefault();
        moveTo(firstEnabled(0, 1));
        return;
      case 'End':
        e.preventDefault();
        moveTo(firstEnabled(options.length - 1, -1));
        return;
      default:
        break;
    }

    // Printable single characters only — modifier combos are shortcuts, not
    // search input.
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      runTypeahead(e.key);
    }
  };

  /* ── render ───────────────────────────────────────────────────────────── */

  const showClear = Boolean(onClear) && !disabled;
  const wrapClass = [
    'select-wrap',
    uiSize === 'sm' ? 'select-wrap--sm' : null,
    showClear ? 'select-wrap--clearable' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const triggerClass = ['select-native', 'dropdown-trigger', triggerClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={wrapClass} style={style}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass}
        style={triggerStyle}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? `${id}-list` : undefined}
        aria-activedescendant={open && active >= 0 ? `${id}-opt-${active}` : undefined}
        aria-label={ariaLabel}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className={selected ? 'dropdown-value' : 'dropdown-value dropdown-value--empty'}>
          {label}
        </span>
      </button>
      {showClear && (
        <button
          type="button"
          className="dropdown-clear"
          aria-label={clearLabel ?? 'Clear'}
          title={clearLabel ?? 'Clear'}
          onClick={onClear}
        >
          <X size={uiSize === 'sm' ? 12 : 14} strokeWidth={2} aria-hidden />
        </button>
      )}
      <ChevronDown
        className="select-chevron"
        size={uiSize === 'sm' ? 12 : 14}
        strokeWidth={2}
        aria-hidden
      />
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={`${id}-list`}
            role="listbox"
            aria-label={ariaLabel}
            className={`dropdown-menu${uiSize === 'sm' ? ' dropdown-menu--sm' : ''}`}
            style={{
              position: 'fixed',
              left: pos.left,
              ...(pos.top !== undefined ? { top: pos.top } : {}),
              ...(pos.bottom !== undefined ? { bottom: pos.bottom } : {}),
              minWidth: pos.minWidth,
              maxWidth: Math.min(MAX_W, window.innerWidth - EDGE * 2),
              maxHeight: pos.maxHeight,
            }}
          >
            {options.map((o, i) => {
              const isSelected = i === selectedIndex;
              return (
                <div
                  key={o.value}
                  id={`${id}-opt-${i}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={o.disabled || undefined}
                  className={[
                    'dropdown-opt',
                    isSelected ? 'is-selected' : null,
                    i === active ? 'is-active' : null,
                    o.disabled ? 'is-disabled' : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  // Pointer and keyboard share ONE highlight, so the mouse
                  // never leaves a second, competing marker behind.
                  onMouseEnter={() => !o.disabled && setActive(i)}
                  onClick={() => commit(i)}
                >
                  <span className="dropdown-opt-label">{o.label}</span>
                  {isSelected && (
                    <Check className="dropdown-opt-check" size={14} strokeWidth={2} aria-hidden />
                  )}
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </span>
  );
});
